import React, { memo } from 'react'
import { Plus, AlertCircle, RotateCcw } from 'lucide-react'
import { Reorder } from 'framer-motion'
import SortableQueueItem from './SortableQueueItem'
import ProcessingQueueItem from './ProcessingQueueItem'
import { getActiveQueue, getHistoryQueue } from '../utils/roomHelpers'
import { cn } from '../lib/utils'
import { Button, Card } from './ui'
import { EmptyQueueState, EmptyHistoryState } from './ui/EmptyState'

const formatCountdown = (rawSeconds) => {
  if (!Number.isFinite(rawSeconds)) return 'Unknown'

  const seconds = Math.max(0, Math.ceil(rawSeconds))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

const formatClockTime = (epochMs) => {
  if (!Number.isFinite(epochMs)) return 'Unknown'
  const target = new Date(epochMs)
  const now = new Date()
  const sameDay = target.toDateString() === now.toDateString()

  return target.toLocaleString([], {
    ...(sameDay
      ? { hour: 'numeric', minute: '2-digit' }
      : { weekday: 'short', hour: 'numeric', minute: '2-digit' })
  })
}

const getSongDurationSeconds = (item) => {
  const duration = item?.song?.duration
  return Number.isFinite(duration) && duration >= 0 ? duration : null
}

// Component for history queue items with thumbnail loading
const HistoryQueueItem = memo(({ item, onRequeue }) => {
  const [thumbnailLoaded, setThumbnailLoaded] = React.useState(false)
  const yt = item.song?.youtubeId ?? null
  const thumbnailUrl = yt
    ? `/uploads/songs/${yt}/thumbnail.webp`
    : null

  React.useEffect(() => {
    if (thumbnailUrl) {
      const img = new Image()
      img.onload = () => {
        setThumbnailLoaded(true)
      }
      img.onerror = () => {
        console.error('🖼️ Failed to load thumbnail for played song:', {
          url: thumbnailUrl,
          songTitle: item.song?.title,
          youtubeId: yt,
          itemId: item.id
        })
        setThumbnailLoaded(false)
      }
      img.src = thumbnailUrl
    }
  }, [thumbnailUrl, item.song?.title, yt, item.id])

  return (
    <Card className="relative overflow-hidden rounded-2xl border border-white/10 bg-[color:rgba(16,27,45,0.58)] p-4 opacity-70 shadow-[0_8px_20px_rgba(2,8,18,0.28)] transition-all duration-200 ease-in-out">
      {/* Thumbnail Background */}
      {thumbnailUrl && thumbnailLoaded && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${thumbnailUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}

      {/* Gradient Overlay - Darker on left for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-[rgba(3,9,18,0.9)] via-[rgba(3,9,18,0.68)] to-[rgba(3,9,18,0.38)]" />

      <div className="relative flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-sm font-medium text-[var(--room-muted)] transition-colors duration-300">
            ✓
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-[var(--room-text)] transition-colors duration-300">
              {item.song?.title}
              <span className="ml-2 rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-xs text-[var(--room-muted)] transition-colors duration-300">
                Played
              </span>
            </h3>
            <p className="text-xs text-[var(--room-muted)] transition-colors duration-300">
              {item.song?.artist}
            </p>
          </div>
        </div>

        {/* Re-queue button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onRequeue(item.songId)}
          className="room-focus-ring shrink-0 rounded-xl border border-white/20 bg-white/5 text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]"
          aria-label="Re-queue song"
          title="Add to queue again"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  )
})

HistoryQueueItem.displayName = 'HistoryQueueItem'

const QueueSection = ({
  queue,
  queueTab,
  setQueueTab,
  waitingCount,
  isDarkMode,
  handleRemoveFromQueue,
  handleRemoveFromProcessing,
  handleQueueReorder,
  processingJobs = new Map(),
  handleRetryProcessingJob,
  queueActionsAvailable = true,
  onSearchClick,
  onRequeue,
  authoritativeIsPlaying = false,
  authoritativePosSeconds = 0,
  authoritativeAnchorPosSec = 0,
  authoritativeAnchorServerMs = 0
}) => {
  const [activeDragId, setActiveDragId] = React.useState(null)
  const [localQueuedSongs, setLocalQueuedSongs] = React.useState([])
  const [nowMs, setNowMs] = React.useState(() => Date.now())
  const propQueueSignatureRef = React.useRef('')
  const dragStartSignatureRef = React.useRef('')
  const currentOrderIdsRef = React.useRef([])
  const isDraggingQueue = activeDragId !== null

  const isAutoQueueJob = (job) => job.autoQueue || (
    Array.isArray(job.autoQueueTargets) && job.autoQueueTargets.length > 0
  )
  const processingQueueJobs = Array.from(processingJobs.values()).filter(
    (job) => ['pending', 'queued', 'processing'].includes(job.status) && isAutoQueueJob(job)
  )
  const failedProcessingJobs = Array.from(processingJobs.values()).filter(
    (job) => job.status === 'failed' && isAutoQueueJob(job)
  )
  const activeQueue = getActiveQueue(queue)
  const playingSong = activeQueue.find(item => item.status === 'playing')
  const queuedSongs = activeQueue.filter(item => item.status !== 'playing')
  const queuedCount = Number.isFinite(waitingCount)
    ? waitingCount
    : queuedSongs.length
  const queuedSongsForRender = React.useMemo(
    () => localQueuedSongs.filter(item => item.id !== playingSong?.id),
    [localQueuedSongs, playingSong?.id]
  )
  const totalQueueItems = queuedSongsForRender.length + processingQueueJobs.length + failedProcessingJobs.length

  React.useEffect(() => {
    if (isDraggingQueue) {
      return undefined
    }

    const timer = window.setInterval(() => {
      setNowMs(Date.now())
    }, 15000)

    return () => window.clearInterval(timer)
  }, [isDraggingQueue])

  React.useEffect(() => {
    const signature = queuedSongs.map(item => item.id).join('|')

    if (queuedSongs.length === 0) {
      if (localQueuedSongs.length !== 0) {
        setLocalQueuedSongs([])
      }
      propQueueSignatureRef.current = ''
      currentOrderIdsRef.current = []
      return
    }

    if (signature !== propQueueSignatureRef.current) {
      propQueueSignatureRef.current = signature
      setLocalQueuedSongs(queuedSongs)
    }
  }, [queuedSongs, localQueuedSongs.length])

  React.useEffect(() => {
    currentOrderIdsRef.current = queuedSongsForRender.map((item) => item.id.toString())
  }, [queuedSongsForRender])

  const timingState = React.useMemo(() => {
    const labelsById = {}
    const orderedActiveSongs = playingSong ? [playingSong, ...localQueuedSongs] : [...localQueuedSongs]

    if (orderedActiveSongs.length === 0) {
      return {
        labelsById,
        totalRemainingLabel: '0:00',
        queueEndClockLabel: null,
        hasPlayableQueue: false
      }
    }

    const anchorBase = Number.isFinite(authoritativeAnchorPosSec)
      ? authoritativeAnchorPosSec
      : (Number.isFinite(authoritativePosSeconds) ? authoritativePosSeconds : 0)
    const anchorMs = Number.isFinite(authoritativeAnchorServerMs) && authoritativeAnchorServerMs > 0
      ? authoritativeAnchorServerMs
      : nowMs

    const elapsedSec = authoritativeIsPlaying ? Math.max(0, (nowMs - anchorMs) / 1000) : 0
    const currentPosSec = Math.max(0, anchorBase + elapsedSec)

    let nextStartSeconds = playingSong ? 0 : 0
    let totalRemainingSeconds = 0
    let totalRemainingKnown = true

    if (playingSong) {
      const playingDuration = getSongDurationSeconds(playingSong)
      const playingRemaining = playingDuration === null
        ? null
        : Math.max(0, playingDuration - currentPosSec)

      if (playingRemaining === null) {
        labelsById[playingSong.id] = authoritativeIsPlaying ? 'Now playing' : 'Paused'
        nextStartSeconds = null
        totalRemainingKnown = false
      } else {
        labelsById[playingSong.id] = authoritativeIsPlaying
          ? `Playing - ${formatCountdown(playingRemaining)} left`
          : `Paused - ${formatCountdown(playingRemaining)} left`
        nextStartSeconds = playingRemaining
        totalRemainingSeconds += playingRemaining
      }
    }

    for (const queuedItem of queuedSongsForRender) {
      labelsById[queuedItem.id] = nextStartSeconds === null
        ? 'Plays after resume'
        : `Plays at: ${formatClockTime(nowMs + (nextStartSeconds * 1000))}`

      const itemDuration = getSongDurationSeconds(queuedItem)
      if (itemDuration === null) {
        nextStartSeconds = null
        totalRemainingKnown = false
      } else {
        if (nextStartSeconds !== null) {
          nextStartSeconds += itemDuration
        }
        totalRemainingSeconds += itemDuration
      }
    }

    return {
      labelsById,
      totalRemainingLabel: totalRemainingKnown ? formatCountdown(totalRemainingSeconds) : 'Unknown',
      queueEndClockLabel: totalRemainingKnown ? formatClockTime(nowMs + (totalRemainingSeconds * 1000)) : null,
      hasPlayableQueue: true
    }
  }, [
    authoritativeAnchorPosSec,
    authoritativePosSeconds,
    authoritativeAnchorServerMs,
    authoritativeIsPlaying,
    nowMs,
    playingSong,
    queuedSongsForRender
  ])

  const handleDragStartInternal = (itemId) => {
    setActiveDragId(itemId)
    dragStartSignatureRef.current = currentOrderIdsRef.current.join('|')
  }

  const handleDragEndInternal = () => {
    window.requestAnimationFrame(() => {
      setActiveDragId(null)

      const startSignature = dragStartSignatureRef.current
      const endOrderIds = currentOrderIdsRef.current
      const endSignature = endOrderIds.join('|')

      dragStartSignatureRef.current = ''

      if (!startSignature || startSignature === endSignature) {
        return
      }

      handleQueueReorder?.(endOrderIds)
    })
  }

  const handleReorderInternal = (nextOrderIds) => {
    setLocalQueuedSongs((currentItems) => {
      const byId = new Map(currentItems.map((item) => [item.id.toString(), item]))
      return nextOrderIds
        .map((id) => byId.get(id.toString()))
        .filter(Boolean)
    })
  }

  const renderActiveQueue = () => {
    if (totalQueueItems === 0) {
      return (
        <EmptyQueueState
          isDarkMode={isDarkMode}
          onAddSong={onSearchClick}
        />
      )
    }

    return (
      <div className="space-y-3">
        {queuedSongsForRender.length > 0 && (
          <Reorder.Group
            axis="y"
            values={queuedSongsForRender.map((item) => item.id.toString())}
            onReorder={handleReorderInternal}
            layoutScroll
            className="space-y-3"
          >
            {queuedSongsForRender.map((item) => (
              <SortableQueueItem
                key={item.id}
                item={item}
                isDarkMode={isDarkMode}
                handleRemoveFromQueue={handleRemoveFromQueue}
                timeLabel={timingState.labelsById[item.id]}
                queueActionsAvailable={queueActionsAvailable}
                isDragging={activeDragId === item.id.toString()}
                onDragStart={handleDragStartInternal}
                onDragEnd={handleDragEndInternal}
              />
            ))}
          </Reorder.Group>
        )}

        {processingQueueJobs.length > 0 && (
          <div className="space-y-3">
            {queuedSongsForRender.length > 0 && (
              <div className="my-4 flex items-center text-xs font-medium text-[var(--room-muted)] transition-colors duration-300">
                <div className="h-px flex-1 bg-white/15" />
                <span className="px-3">Processing</span>
                <div className="h-px flex-1 bg-white/15" />
              </div>
            )}

            {processingQueueJobs.map((job, index) => (
              <ProcessingQueueItem
                key={job.jobId}
                job={job}
                index={queuedSongsForRender.length + index}
                isDarkMode={isDarkMode}
                handleRemoveFromProcessing={handleRemoveFromProcessing}
              />
            ))}
          </div>
        )}

        {failedProcessingJobs.length > 0 && (
          <div className="space-y-3">
            <div className="my-4 flex items-center text-xs font-medium text-[var(--room-danger)] transition-colors duration-300">
              <div className="h-px flex-1 bg-[color:rgba(255,102,122,0.35)]" />
              <span className="px-3">Needs retry</span>
              <div className="h-px flex-1 bg-[color:rgba(255,102,122,0.35)]" />
            </div>

            {failedProcessingJobs.map((job) => {
              const failedSongTitle = job.song?.title || 'Submitted song'
              return (
                <Card
                  key={job.jobId}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-[color:rgba(255,102,122,0.35)] bg-[color:rgba(255,102,122,0.12)] p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[var(--room-text)]">
                      {failedSongTitle}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-[var(--room-danger)]">
                      <AlertCircle className="h-3.5 w-3.5" />
                      Processing failed. Try again.
                    </p>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleRetryProcessingJob?.(job.jobId)}
                    className="room-focus-ring h-11 rounded-xl border border-[color:rgba(255,102,122,0.4)] bg-[color:rgba(255,102,122,0.2)] text-[var(--room-danger)] hover:bg-[color:rgba(255,102,122,0.3)]"
                    aria-label={`Retry processing ${failedSongTitle}`}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Retry
                  </Button>
                </Card>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const renderHistoryQueue = () => {
    const historyQueue = getHistoryQueue(queue)

    if (historyQueue.length === 0) {
      return <EmptyHistoryState isDarkMode={isDarkMode} />
    }

    return (
      <div className="space-y-3">
        {historyQueue.map((item) => (
          <HistoryQueueItem
            key={item.id}
            item={item}
            onRequeue={onRequeue}
          />
        ))}
      </div>
    )
  }

  return (
    <>
      <Card
        className="room-surface rounded-3xl p-4 transition-colors duration-300"
        data-tutorial-id="queue-section"
      >
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="inline-grid w-fit grid-cols-2 rounded-2xl border border-white/20 bg-black/20 p-1">
            <button
              type="button"
              onClick={() => setQueueTab('queue')}
              className={cn(
                'room-focus-ring flex h-11 min-w-[132px] items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold transition-colors duration-200',
                queueTab === 'queue'
                  ? 'bg-[linear-gradient(140deg,rgba(46,168,255,0.42),rgba(46,168,255,0.24))] text-[var(--room-text)] shadow-[0_8px_18px_rgba(46,168,255,0.22)]'
                  : 'text-[var(--room-muted)] hover:bg-white/5 hover:text-[var(--room-text)]'
              )}
            >
              <span>Queue</span>
            </button>

            <button
              type="button"
              onClick={() => setQueueTab('history')}
              className={cn(
                'room-focus-ring flex h-11 min-w-[132px] items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold transition-colors duration-200',
                queueTab === 'history'
                  ? 'bg-[linear-gradient(140deg,rgba(46,168,255,0.42),rgba(46,168,255,0.24))] text-[var(--room-text)] shadow-[0_8px_18px_rgba(46,168,255,0.22)]'
                  : 'text-[var(--room-muted)] hover:bg-white/5 hover:text-[var(--room-text)]'
              )}
            >
              <span>History</span>
            </button>
          </div>

          {queueTab === 'queue' ? (
            <div className="flex flex-col items-end gap-2 text-xs sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              {queuedCount >= 5 ? (
                <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 font-semibold text-[var(--room-muted)]">
                  {queuedCount} queued
                </span>
              ) : null}
              {timingState.hasPlayableQueue ? (
                <span className="rounded-full border border-[color:rgba(46,168,255,0.35)] bg-[color:rgba(46,168,255,0.14)] px-2.5 py-1 font-semibold text-[var(--room-primary)]">
                  {timingState.queueEndClockLabel ? `Ends at ${timingState.queueEndClockLabel}` : 'End time unknown'}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div>
          {queueTab === 'queue' ? renderActiveQueue() : renderHistoryQueue()}
        </div>
      </Card>
    </>
  )
}

export default QueueSection
